import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useState } from 'react'
import { Select } from 'shared/ui/Select/Select'
import { Button, EnumDropdownItemVariant, type IDropdownItem } from 'shared/ui'
import type { IFilterValue } from 'entities/Filters/types'
import { BroadcastStatus, type IBroadcastStatus } from 'entities/Broadcast'
import type { IWidgetProps } from 'widgets/FilterEditor/types/types'
import { WidgetBroadcastStatusTrigger } from './WidgetBroadcastStatusTrigger'
import styles from './styles.module.scss'

export const WidgetBroadcastStatus = observer(
  ({
    filterItem,
    expandByDefault = false,
    parentsDropdownId,
  }: IWidgetProps<IFilterValue<IBroadcastStatus>, IBroadcastStatus[]>) => {
    const [showDropdown, setShowDropdown] = useState(false)
    const [showValuesWarning, setShowValuesWarning] = useState(false)
    const filterConfig = filterItem.config
    const values = filterConfig.values ?? []
    const payloadValue = filterItem.payload.value ?? []
    const [primaryKeys, setPrimaryKeys] = useState<IBroadcastStatus[]>([])
    const [selectedKeys, setSelectedKeys] = useState<IBroadcastStatus[]>(payloadValue)
    const dropdownItems: IDropdownItem[] = useMemo(
      () =>
        values
          .reduce<[IFilterValue<IBroadcastStatus>[], IFilterValue<IBroadcastStatus>[]]>(
            (values, value) => {
              const isSelected = primaryKeys.includes(value.key)

              if (isSelected) values[0].push(value)
              else values[1].push(value)

              return values
            },
            [[], []]
          )
          .flat()
          .map((item, index) => {
            const key = item.key

            return {
              id: item.key,
              label: item.label,
              labelContent: <BroadcastStatus status={key} />,
              variant: EnumDropdownItemVariant.Checkbox,
              activeValue: selectedKeys.includes(key),
              divider: index === primaryKeys.length - 1 && primaryKeys.length !== values.length,
            }
          }),
      [selectedKeys, primaryKeys]
    )

    useEffect(() => {
      setPrimaryKeys(payloadValue)
    }, [])

    useEffect(() => {
      setTimeout(() => setShowDropdown(expandByDefault), 100)
    }, [expandByDefault])

    return (
      <Select
        text={filterItem.operator?.key ?? ''}
        type='multi'
        items={dropdownItems}
        dropdownProps={{
          ariaLabel: 'Select',
          parentsDropdownId: parentsDropdownId,
          show: showDropdown,
          textFieldProps: {
            InputProps: { placeholder: `Search ${filterItem.config.name}` },
          },
          bottomActions: selectedKeys.length
            ? () => (
                <Button
                  text='Clear'
                  contained='secondary'
                  typeBtn='text'
                  onClick={() => {
                    setPrimaryKeys([])
                    setSelectedKeys([])
                    filterItem.payload.updateHard({ value: [] })
                  }}
                />
              )
            : undefined,
          className: styles.operatorDropdown,
          withSearch: true,
          onClose: () => {
            setPrimaryKeys(selectedKeys)
            setShowValuesWarning(!selectedKeys.length)
          },
          triggerComponent: () => (
            <div
              className={classNames(styles.selectTriggerComponent, {
                [styles.warning]: showValuesWarning,
              })}
            >
              <WidgetBroadcastStatusTrigger items={selectedKeys} />
            </div>
          ),
          onChange: (item) => {
            const id = item.id as IBroadcastStatus
            const keys = item.activeValue
              ? selectedKeys.filter((key) => key !== id)
              : [...selectedKeys, id]

            setShowValuesWarning(false)
            setSelectedKeys(keys)
            filterItem.payload.updateHard({ value: keys })
          },
        }}
      />
    )
  }
)
